import type { Security } from '@ipfabric/utils';
import { nullable } from '@ipfabric/utils/fp/nullable';
import { prop } from '@ipfabric/utils/fp/prop';
import { getRuleChain } from 'components/SecuritySchema/helpers/getRuleChain';
import type { SecuritySchemaMergedState } from 'components/SecuritySchema/model/SecuritySchemaModel';
import type {
  PolicyNode,
  RuleNode,
  SecuritySchemaNode,
} from 'components/SecuritySchema/model/SecuritySchemaNode';
import { securitySchemaNodeFromRule } from 'components/SecuritySchema/model/helpers/securitySchemaNodeFromRule';
import type { SecurityNodeResolutionScope } from 'components/SecuritySchema/model/helpers/securitySchemaNodeFromTest';
import { selectRuleChainScope } from 'components/SecuritySchema/model/selectors/selectRuleChainScope';
import { array } from 'fp-ts';
import { pipe } from 'fp-ts/lib/function';
import { createSelector } from 'reselect';
import { createScopedSelector } from 'utils/react/scopedSelector';

const securitySchemaNodeFromDefaultAction = (
  defaultAction: Security.Action,
): RuleNode => ({
  active: true,
  action: defaultAction,
  children: array.empty,
  defaultAction: true,
  type: 'rule',
});

/**
 * A cloud enforcement point evaluated in stages (GCP, whose `goto_next` skips to
 * the next policy) holds one `exec` per policy instead of a flat rule list, each
 * tagged with the policy it runs. Render those as the policy's box — same
 * vocabulary as the merged vendors — not as an `exec` rule. The box is
 * deliberately empty; its rules are the next page down.
 */
const cloudPolicyNodeFromExecRule = (rule: Security.Rule): PolicyNode | null =>
  rule.action.type === 'exec' && rule.cloudPolicy !== undefined
    ? {
        chainName: rule.action.chainName,
        children: array.empty,
        policy: rule.cloudPolicy,
        type: 'policy',
      }
    : null;

const getRuleChainRootNodes =
  (resolutionScope: SecurityNodeResolutionScope) =>
  (ruleChain: Security.RuleChain): SecuritySchemaNode[] => [
    ...ruleChain.rules.map(
      (rule) =>
        cloudPolicyNodeFromExecRule(rule) ??
        securitySchemaNodeFromRule(resolutionScope)(rule),
    ),
    securitySchemaNodeFromDefaultAction(ruleChain.defaultAction),
  ];

export const selectActiveRuleChainSchemaRoots: (
  s: SecuritySchemaMergedState,
) => SecuritySchemaNode[] | null = createScopedSelector(() =>
  createSelector(
    prop('deviceSecurity'),
    selectRuleChainScope,
    (deviceSecurity, scope) =>
      deviceSecurity !== null && scope !== null
        ? pipe(
            deviceSecurity,
            getRuleChain(scope),
            nullable.chain(
              getRuleChainRootNodes({
                deviceSecurity,
                type: scope.securityType,
              }),
            ),
          )
        : null,
  ),
);
